import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ColumnFilterRow, SortableHeaderRow, TableToolbar, useTableTools } from "@/components/TableFilters";
import api, { fmtMXN, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus } from "lucide-react";

const LABEL = { borrador: "Borrador", enviada: "Enviada", aceptada: "Aceptada", surtido: "En preparación", enviado: "Enviado", cerrado: "Cerrado", perdida: "No adjudicada", vencida: "Vencida" };
const BADGE = { borrador: "secondary", enviada: "default", aceptada: "outline", surtido: "outline", enviado: "outline", cerrado: "outline", perdida: "destructive", vencida: "secondary" };

const QSTATUS = [["borrador", "Borrador"], ["enviada", "Enviada"], ["aceptada", "Aceptada"], ["surtido", "En preparación"], ["enviado", "Enviado"], ["cerrado", "Cerrado"], ["perdida", "No adjudicada"], ["vencida", "Vencida"]];
const COLUMNS = [
  { key: "folio", label: "Folio", accessor: (q) => q.folio, placeholder: "Folio" },
  { key: "cliente", label: "Cliente", accessor: (q) => q.client_snapshot?.razon_social, placeholder: "Cliente" },
  { key: "total", label: "Total", accessor: (q) => q.total, placeholder: "Total" },
  { key: "margen", label: "Margen", accessor: (q) => q.margen_pct, placeholder: "%" },
  { key: "estatus", label: "Estatus", type: "select", accessor: (q) => q.estatus, options: QSTATUS.map(([value, label]) => ({ value, label })) },
  { key: "fecha", label: "Fecha", accessor: (q) => fmtDate(q.created_at), sortAccessor: (q) => q.created_at, placeholder: "Fecha" },
];

export default function Quotations() {
  const [rows, setRows] = useState([]);
  const nav = useNavigate();
  useEffect(() => { api.get("/quotations").then((r) => setRows(r.data)); }, []);
  const tools = useTableTools(rows, COLUMNS);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-3xl font-heading font-bold tracking-tight">Cotizaciones</h1><p className="text-muted-foreground text-sm">Folios correlativos con capa de costos y margen.</p></div>
        <Button onClick={() => nav("/app/cotizaciones/nueva")} data-testid="new-quote-btn"><Plus className="w-4 h-4 mr-2" /> Nueva cotización</Button>
      </div>
      <div className="flex items-center justify-end"><TableToolbar tools={tools} columns={COLUMNS} filename="cotizaciones" /></div>
      <Card className="rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <SortableHeaderRow columns={COLUMNS} sort={tools.sort} toggleSort={tools.toggleSort} />
            {tools.showFilters && <ColumnFilterRow columns={COLUMNS} filters={tools.filters} setFilters={tools.setFilters} />}
          </TableHeader>
          <TableBody>
            {tools.view.map((q) => (
              <TableRow key={q.id} className="cursor-pointer" onClick={() => nav(`/app/cotizaciones/${q.id}`)} data-testid="quote-row">
                <TableCell className="font-mono font-medium">{q.folio}</TableCell>
                <TableCell>{q.client_snapshot?.razon_social}</TableCell>
                <TableCell>{fmtMXN(q.total)}</TableCell>
                <TableCell className={q.margen_pct < 15 ? "text-red-500 font-medium" : "text-emerald-600 font-medium"}>{q.margen_pct}%</TableCell>
                <TableCell><Badge variant={BADGE[q.estatus] || "outline"}>{LABEL[q.estatus] || q.estatus}</Badge></TableCell>
                <TableCell className="text-muted-foreground">{fmtDate(q.created_at)}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Sin cotizaciones aún.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
